import { useState, useEffect } from 'react';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { bookingsApi, ApiRequestError, type Booking } from '@/lib/api';

export default function PendingBookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [decisionModal, setDecisionModal] = useState<{
    booking: Booking;
    action: 'approve' | 'decline';
  } | null>(null);
  const [decisionMessage, setDecisionMessage] = useState('');
  const [notesModal, setNotesModal] = useState<Booking | null>(null);
  const [notes, setNotes] = useState('');

  const fetchBookings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { bookings } = await bookingsApi.getPending();
      setBookings(bookings);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load pending bookings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleApprove = async () => {
    if (!decisionModal || decisionModal.action !== 'approve') return;

    setActionLoading(decisionModal.booking.id);
    try {
      await bookingsApi.approve(decisionModal.booking.id, decisionMessage.trim() || undefined);
      setDecisionModal(null);
      setDecisionMessage('');
      await fetchBookings();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to approve booking');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    if (!decisionModal || decisionModal.action !== 'decline') return;

    setActionLoading(decisionModal.booking.id);
    try {
      await bookingsApi.decline(decisionModal.booking.id, decisionMessage.trim() || undefined);
      setDecisionModal(null);
      setDecisionMessage('');
      await fetchBookings();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to decline booking');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateNotes = async () => {
    if (!notesModal) return;

    setActionLoading(notesModal.id);
    try {
      await bookingsApi.updateNotes(notesModal.id, notes);
      setNotesModal(null);
      setNotes('');
      await fetchBookings();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to update notes');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), 'MMM d, yyyy');
    const end = format(parseISO(endDate), 'MMM d, yyyy');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getNights = (startDate: string, endDate: string) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    return Math.max(0, differenceInCalendarDays(end, start));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Pending Requests</h1>
        {bookings.length > 0 && (
          <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-3 py-1 rounded-full">
            {bookings.length} pending
          </span>
        )}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-6 text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading pending requests...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center py-12">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchBookings}
              className="mt-4 text-primary-600 hover:text-primary-700"
            >
              Try again
            </button>
          </div>
        ) : bookings.length === 0 ? (
          <div className="p-6 text-center py-12 text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No Pending Requests
            </h3>
            <p className="mt-2 text-sm">
              All booking requests have been processed.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {bookings.map((booking) => (
              <div key={booking.id} className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    {/* User info */}
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                        style={{ backgroundColor: booking.user_color || '#3B82F6' }}
                      >
                        {booking.user_name?.charAt(0).toUpperCase() || '?'}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {booking.user_name || 'Unknown User'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Requested {format(parseISO(booking.created_at), 'MMM d, yyyy \'at\' h:mm a')}
                        </p>
                      </div>
                    </div>

                    {/* Date range */}
                    <div className="mt-4 flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="font-medium text-gray-900">
                        {formatDateRange(booking.start_date, booking.end_date)}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({getNights(booking.start_date, booking.end_date)} night
                        {getNights(booking.start_date, booking.end_date) !== 1 ? 's' : ''})
                      </span>
                    </div>

                    {/* Arrival/departure times */}
                    {booking.start_time && booking.end_time && (
                      <p className="mt-2 text-sm text-gray-500">
                        Arrival: {booking.start_time} / Departure: {booking.end_time}
                      </p>
                    )}

                    {/* User message */}
                    {booking.user_message && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Guest message:</span>{' '}
                          {booking.user_message}
                        </p>
                      </div>
                    )}

                    {/* Owner notes */}
                    <div className="mt-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">Admin Notes:</span>
                        <button
                          onClick={() => {
                            setNotes(booking.owner_notes || '');
                            setNotesModal(booking);
                          }}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          {booking.owner_notes ? 'Edit' : 'Add'}
                        </button>
                      </div>
                      {booking.owner_notes && (
                        <p className="mt-1 text-sm text-gray-600">{booking.owner_notes}</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setDecisionMessage('');
                        setDecisionModal({ booking, action: 'approve' });
                      }}
                      disabled={actionLoading === booking.id}
                      className="btn-success"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setDecisionMessage('');
                        setDecisionModal({ booking, action: 'decline' });
                      }}
                      disabled={actionLoading === booking.id}
                      className="btn-danger"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Decision Modal */}
      {decisionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setDecisionModal(null)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {decisionModal.action === 'approve' ? 'Approve Booking' : 'Decline Booking'}
              </h2>

              <div className="mb-4">
                <p className="text-gray-600">
                  <strong>{decisionModal.booking.user_name}</strong> requested{' '}
                  {formatDateRange(decisionModal.booking.start_date, decisionModal.booking.end_date)}
                </p>
              </div>

              <div className="mb-4">
                <label htmlFor="decision-message" className="label">
                  Message to guest (optional)
                </label>
                <textarea
                  id="decision-message"
                  value={decisionMessage}
                  onChange={(e) => setDecisionMessage(e.target.value)}
                  className="input min-h-[80px]"
                  placeholder={
                    decisionModal.action === 'approve'
                      ? 'e.g., Looking forward to your stay!'
                      : 'e.g., Sorry, those dates are not available...'
                  }
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDecisionModal(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={decisionModal.action === 'approve' ? handleApprove : handleDecline}
                  disabled={actionLoading === decisionModal.booking.id}
                  className={`flex-1 ${
                    decisionModal.action === 'approve' ? 'btn-success' : 'btn-danger'
                  }`}
                >
                  {actionLoading === decisionModal.booking.id
                    ? 'Processing...'
                    : decisionModal.action === 'approve'
                    ? 'Confirm Approve'
                    : 'Confirm Decline'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {notesModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setNotesModal(null)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Admin Notes
              </h2>

              <div className="mb-4">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input min-h-[100px]"
                  placeholder="Add private notes about this booking..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  These notes are only visible to owners and admins.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setNotesModal(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateNotes}
                  disabled={actionLoading === notesModal.id}
                  className="btn-primary flex-1"
                >
                  {actionLoading === notesModal.id ? 'Saving...' : 'Save Notes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
